// =============================================================================
// features/assistant/hooks/useEdgeTabPosition.ts — Posição vertical da aba do
// copiloto: arrasto, teclado, reclamp e persistência (F51-S37).
//
// O que se guarda é a FRAÇÃO do viewport (centro da aba / altura), não o px:
// ela sobrevive a rotação e a barras de endereço. O px renderizado é sempre
// recalculado e preso aos limites atuais — reclampar NÃO regrava a preferência,
// então a aba volta ao lugar escolhido quando o viewport cresce de novo.
// =============================================================================

import * as React from 'react';

import {
  TAB_POSITION_STORAGE_KEY,
  clampTabTop,
  computeTabBounds,
  fractionFromTabTop,
  isDragGesture,
  parseStoredTabFraction,
  serializeTabFraction,
  stepTabTop,
  tabTopFromFraction,
  type TabViewport,
} from '../edgeTab';

function readStoredFraction(): number {
  try {
    return parseStoredTabFraction(window.localStorage.getItem(TAB_POSITION_STORAGE_KEY));
  } catch {
    // Storage bloqueado (modo privado, política do navegador): usa o padrão.
    return parseStoredTabFraction(null);
  }
}

function writeStoredFraction(fraction: number): void {
  try {
    window.localStorage.setItem(TAB_POSITION_STORAGE_KEY, serializeTabFraction(fraction));
  } catch {
    // Sem storage a posição vale só nesta sessão — nunca quebra a tela.
  }
}

/** Resolve `env(safe-area-inset-*)` em px com um elemento-sonda invisível. */
function readViewport(): TabViewport {
  const probe = document.createElement('div');
  probe.style.cssText =
    'position:fixed;top:0;left:0;width:0;height:0;visibility:hidden;pointer-events:none;' +
    'padding-top:env(safe-area-inset-top,0px);padding-bottom:env(safe-area-inset-bottom,0px)';
  document.body.appendChild(probe);
  const style = window.getComputedStyle(probe);
  const safeTop = Number.parseFloat(style.paddingTop);
  const safeBottom = Number.parseFloat(style.paddingBottom);
  probe.remove();
  return { height: window.innerHeight, safeTop, safeBottom };
}

interface Gesture {
  pointerId: number;
  startY: number;
  startTop: number;
  lastTop: number;
  dragging: boolean;
}

export interface EdgeTabPosition {
  top: number;
  dragging: boolean;
  pressed: boolean;
  onPointerDown: (event: React.PointerEvent<HTMLButtonElement>) => void;
  onPointerMove: (event: React.PointerEvent<HTMLButtonElement>) => void;
  onPointerUp: (event: React.PointerEvent<HTMLButtonElement>) => void;
  onPointerCancel: (event: React.PointerEvent<HTMLButtonElement>) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  /** true se o click que chega agora é o fim de um arrasto (não deve abrir). */
  consumeDragClick: () => boolean;
}

export function useEdgeTabPosition(): EdgeTabPosition {
  const [fraction, setFraction] = React.useState(readStoredFraction);
  const [viewport, setViewport] = React.useState<TabViewport>(() => ({
    height: window.innerHeight,
    safeTop: 0,
    safeBottom: 0,
  }));
  const [dragTop, setDragTop] = React.useState<number | null>(null);
  const [pressed, setPressed] = React.useState(false);
  const gesture = React.useRef<Gesture | null>(null);
  const suppressClick = React.useRef(false);

  // Reclampa ao redimensionar, girar e quando o visualViewport muda.
  React.useEffect(() => {
    let frame = 0;
    const schedule = (): void => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => setViewport(readViewport()));
    };
    schedule();
    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', schedule);
    window.visualViewport?.addEventListener('resize', schedule);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('orientationchange', schedule);
      window.visualViewport?.removeEventListener('resize', schedule);
    };
  }, []);

  const bounds = computeTabBounds(viewport);
  const restingTop = tabTopFromFraction(fraction, viewport);
  const top = dragTop ?? restingTop;

  const commit = (nextTop: number): void => {
    const next = fractionFromTabTop(clampTabTop(nextTop, bounds), viewport.height);
    setFraction(next);
    writeStoredFraction(next);
  };

  const endGesture = (event: React.PointerEvent<HTMLButtonElement>, isUp: boolean): void => {
    const current = gesture.current;
    if (!current || current.pointerId !== event.pointerId) return;
    gesture.current = null;
    setPressed(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (!current.dragging) return;
    commit(current.lastTop);
    setDragTop(null);
    // Só pointerup gera click depois; pointercancel não.
    suppressClick.current = isUp;
  };

  return {
    top,
    dragging: dragTop !== null,
    pressed,
    onPointerDown: (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      suppressClick.current = false;
      gesture.current = {
        pointerId: event.pointerId,
        startY: event.clientY,
        startTop: restingTop,
        lastTop: restingTop,
        dragging: false,
      };
      setPressed(true);
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    onPointerMove: (event) => {
      const current = gesture.current;
      if (!current || current.pointerId !== event.pointerId) return;
      const deltaY = event.clientY - current.startY;
      if (!current.dragging) {
        if (!isDragGesture(deltaY)) return;
        current.dragging = true;
      }
      current.lastTop = clampTabTop(current.startTop + deltaY, bounds);
      setDragTop(current.lastTop);
    },
    onPointerUp: (event) => endGesture(event, true),
    onPointerCancel: (event) => endGesture(event, false),
    onKeyDown: (event) => {
      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
      event.preventDefault();
      commit(stepTabTop(restingTop, event.key === 'ArrowUp' ? 'up' : 'down', bounds));
    },
    consumeDragClick: () => {
      const suppressed = suppressClick.current;
      suppressClick.current = false;
      return suppressed;
    },
  };
}
